import React, { useEffect, useRef, useState } from 'react';

/** Topbar du cockpit — structure et VRAIES classes du prototype (`.topbar`). */

/** Un centre d'appel (bâtiment de type CTA) proposé dans le sélecteur de périmètre. */
export interface CtaOption {
  id: number;
  nom: string;
  ind: string; // code court affiché dans la pastille (ex. « CTA », initiales)
  sites: number; // bâtiments rattachés à ce CTA
  engins: number; // engins rattachés à ce CTA
  actif: boolean;
}

export type Scope = 'all' | number;

interface Props {
  currentUser:
    | { username?: string; argents?: number | null; medailles?: number | null; roles_id?: number | null }
    | null
    | undefined;
  priseAppels: boolean;
  onTogglePrise: () => void;
  /** Liste des centres d'appel du joueur. */
  ctaList: CtaOption[];
  /** Périmètre courant : 'all' ou l'id d'un CTA. */
  scope: Scope;
  onScopeChange: (v: Scope) => void;
  /** Compteur du périmètre courant, ex. « 14 sites · 22 engins ». */
  scopeCount: string;
  /** Bloc multi-écrans (rôle + bouton « Écran »), injecté par le cockpit. */
  screensSlot?: React.ReactNode;
  /** Ouvre la palette de commandes (bouton « Commandes » / Ctrl+K). */
  onOpenPalette?: () => void;
  onPanel: () => void;
  onDashboard: () => void;
}

const roleLabel = (r?: number | null): string => {
  switch (Number(r)) {
    case 1:
      return 'Community manager';
    case 2:
      return 'Administrateur';
    case 3:
      return 'Modérateur';
    case 4:
      return 'Groupe de réflexion';
    default:
      return 'Chef de salle';
  }
};

const CockpitTopbar: React.FC<Props> = ({
  currentUser,
  priseAppels,
  onTogglePrise,
  ctaList,
  scope,
  onScopeChange,
  scopeCount,
  screensSlot,
  onOpenPalette,
  onPanel,
  onDashboard,
}) => {
  const now = new Date();
  const clock = now.toLocaleTimeString('fr-FR');
  const date = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const showPanel = Number(currentUser?.roles_id ?? 99) <= 4;

  const [scopeOpen, setScopeOpen] = useState(false);
  const scopeRef = useRef<HTMLDivElement>(null);

  // Fermeture au clic extérieur / Échap.
  useEffect(() => {
    if (!scopeOpen) return;
    const onDown = (e: MouseEvent) => {
      if (scopeRef.current && !scopeRef.current.contains(e.target as Node)) setScopeOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setScopeOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [scopeOpen]);

  const curCta = scope === 'all' ? null : ctaList.find((c) => c.id === scope) || null;
  const scopeInd = curCta ? curCta.ind : 'TOUS';
  const scopeNom = curCta ? curCta.nom : `${ctaList.length} centre${ctaList.length > 1 ? 's' : ''} d'appel`;

  const pick = (v: Scope) => {
    onScopeChange(v);
    setScopeOpen(false);
  };

  return (
    <header className="topbar">
      <div className="topbar__brand">
        <div className="logo">OS</div>
        <div>
          <p className="topbar__eyebrow">Salle opérationnelle</p>
          <p className="topbar__title">
            OPSECOURS <span className="topbar__sector">· {currentUser?.username || '—'}</span>
          </p>
        </div>
      </div>

      <div className="scope" ref={scopeRef}>
        <label>Périmètre</label>
        <button
          className="scope__btn"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={scopeOpen}
          onClick={() => setScopeOpen((v) => !v)}
        >
          <span className="scope__ind mono">{scopeInd}</span>
          <span className="scope__nom">{scopeNom}</span>
          <span className="scope__caret">▾</span>
        </button>
        <span className="scope__count mono">{scopeCount}</span>

        {scopeOpen ? (
          <div className="scope__menu" role="listbox">
            <button
              className={`sitem ${scope === 'all' ? 'is-cur' : ''}`}
              type="button"
              onClick={() => pick('all')}
            >
              <span className="sitem__ind mono">TOUS</span>
              <span className="sitem__body">
                <span className="sitem__nom">Tous les centres d'appel</span>
                <span className="sitem__sub">{ctaList.length} CTA</span>
              </span>
            </button>
            {ctaList.length ? <div className="sitem__sep" /> : null}
            {ctaList.map((c) => (
              <button
                key={c.id}
                className={`sitem ${scope === c.id ? 'is-cur' : ''} ${!c.actif ? 'is-dead' : ''}`}
                type="button"
                onClick={() => pick(c.id)}
              >
                <span className="sitem__ind mono">{c.ind}</span>
                <span className="sitem__body">
                  <span className="sitem__nom">{c.nom}</span>
                  <span className="sitem__sub">{c.sites} site(s) · {c.engins} engin(s)</span>
                </span>
                <span className="sitem__badges">
                  {c.actif ? (
                    <span className="cbadge cbadge--on">Actif</span>
                  ) : (
                    <span className="cbadge cbadge--off">Hors service</span>
                  )}
                </span>
              </button>
            ))}
            {ctaList.length === 0 ? (
              <div className="empty" style={{ padding: 10 }}>
                Aucun centre d'appel : construisez un bâtiment de type CTA.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="topbar__clock">
        <span className="clock__time mono">{clock}</span>
        <span className="clock__date">{date}</span>
      </div>

      <div className="topbar__right">
        <div className="chip chip--money">€ <b>{Number(currentUser?.argents ?? 0).toLocaleString('fr-FR')}</b></div>
        <div className="chip chip--medal">🏅 <b>{Number(currentUser?.medailles ?? 0).toLocaleString('fr-FR')}</b></div>

        <button
          className="toggle"
          type="button"
          data-on={priseAppels ? 'true' : 'false'}
          onClick={onTogglePrise}
          title="Prise d'appels — OFF par défaut, pilotée par le joueur"
        >
          <span className="toggle__label">Prise d&apos;appels</span>
          <span className="toggle__switch"><i /></span>
          <span className="toggle__state">{priseAppels ? 'ON' : 'OFF'}</span>
        </button>

        {screensSlot}

        {onOpenPalette ? (
          <button className="kbd-btn" type="button" onClick={onOpenPalette} title="Palette de commandes">
            <span>Commandes</span><kbd>Ctrl</kbd><kbd>K</kbd>
          </button>
        ) : null}

        {showPanel ? (
          <button className="panel-btn" type="button" onClick={onPanel}>
            <svg viewBox="0 0 24 24"><path d="M3 4h18v4H3V4Zm0 6h8v10H3V10Zm10 0h8v4h-8v-4Zm0 6h8v4h-8v-4Z" /></svg>
            <span>Panel</span><kbd>P</kbd>
          </button>
        ) : null}

        <button className="operator" type="button" onClick={onDashboard} title="Retour au dashboard">
          <span className="operator__dot" />
          <div>
            <p className="operator__name">{(currentUser?.username || '—').toUpperCase()}</p>
            <p className="operator__role">{roleLabel(currentUser?.roles_id)}</p>
          </div>
        </button>
      </div>
    </header>
  );
};

export default CockpitTopbar;
